/**
 * PD-17066 — Competitors: the rules both the server and the screens need.
 *
 * Competitors live in the `wholesaler` table and a customer's relationship
 * with one in `client_wholesaler`. SM1 and the ERP imports write the link with
 * only a `tier` code, so the relationship is read through
 * {@link resolveCompetitorRelationship}, and every SM2 write keeps `tier` and
 * `relationship` in step through {@link competitorTierForRelationship}.
 */

/**
 * PD-17643: how directly a company competes with us, carried over from the
 * conference planner. It groups the Competitors tab on a trade show.
 */
export const COMPETITOR_LEVELS = ['direct', 'partial', 'adjacent', 'partner'] as const;

export type CompetitorLevelModel = (typeof COMPETITOR_LEVELS)[number];

export const COMPETITOR_LEVEL_LABELS: Readonly<Record<CompetitorLevelModel, string>> = {
	direct: 'Direct Competitor',
	partial: 'Partial Competitor',
	adjacent: 'Adjacent',
	partner: 'Partner',
};

export const COMPETITOR_LEVEL_GROUP_LABELS: Readonly<Record<CompetitorLevelModel, string>> = {
	direct: 'Direct Competitors',
	partial: 'Partial Competitors',
	adjacent: 'Adjacent Companies',
	partner: 'Partners',
};

export function isCompetitorLevel(value: unknown): value is CompetitorLevelModel {
	return COMPETITOR_LEVELS.includes(value as CompetitorLevelModel);
}

export const COMPETITOR_RELATIONSHIPS = [
	'primary',
	'secondary',
	'also_buys',
	'former',
] as const;

export type CompetitorRelationshipModel =
	(typeof COMPETITOR_RELATIONSHIPS)[number];

export const COMPETITOR_RELATIONSHIP_LABELS: Readonly<
	Record<CompetitorRelationshipModel, string>
> = {
	primary: 'Primary',
	secondary: 'Secondary',
	also_buys: 'Also buys from',
	former: 'Former',
};

/**
 * SM1's `tier` code for each relationship. SM1 has always written '1' and '2'
 * (primary, secondary). '3' and '4' are new with PD-17066; SM1 reads `tier`
 * only as text in its client search index and never interprets it, so the new
 * codes are safe there.
 */
export const COMPETITOR_TIER_BY_RELATIONSHIP: Readonly<
	Record<CompetitorRelationshipModel, string>
> = {
	primary: '1',
	secondary: '2',
	also_buys: '3',
	former: '4',
};

export function isCompetitorRelationship(
	value: unknown,
): value is CompetitorRelationshipModel {
	return (
		typeof value === 'string' &&
		(COMPETITOR_RELATIONSHIPS as ReadonlyArray<string>).includes(value)
	);
}

export function competitorTierForRelationship(
	relationship: CompetitorRelationshipModel,
): string {
	return COMPETITOR_TIER_BY_RELATIONSHIP[relationship];
}

/**
 * The relationship a tier code stands for, or null for a code SM2 does not
 * know. Seeded pharmacy data also spells the first two out ("primary",
 * "secondary"), so both spellings are read.
 */
export function competitorRelationshipFromTier(
	tier: string | null | undefined,
): CompetitorRelationshipModel | null {
	const code = (tier ?? '').trim().toLowerCase();

	switch (code) {
		case '1':
		case 'primary':
			return 'primary';
		case '2':
		case 'secondary':
			return 'secondary';
		case '3':
			return 'also_buys';
		case '4':
			return 'former';
		default:
			return null;
	}
}

/**
 * The relationship of one link. A known `tier` code wins over `relationship`,
 * because SM1 and the imports change only `tier`: a link SM2 wrote as primary
 * and SM1 later moved to tier '2' is secondary. `relationship` decides only
 * when the tier code is unknown, and a link with neither reads as "also buys
 * from" (a supplier with no stated rank). Keep in step with
 * {@link competitorRelationshipSql}.
 */
export function resolveCompetitorRelationship(
	relationship: string | null | undefined,
	tier: string | null | undefined,
): CompetitorRelationshipModel {
	return (
		competitorRelationshipFromTier(tier) ??
		(isCompetitorRelationship(relationship) ? relationship : 'also_buys')
	);
}

/**
 * {@link resolveCompetitorRelationship} as a SQL expression over a
 * `client_wholesaler` alias. Trusted SQL built from a literal alias; never
 * pass request input.
 */
export function competitorRelationshipSql(linkAlias: string): string {
	return `(CASE
		WHEN TRIM(${linkAlias}.tier) IN ('1', 'primary') THEN 'primary'
		WHEN TRIM(${linkAlias}.tier) IN ('2', 'secondary') THEN 'secondary'
		WHEN TRIM(${linkAlias}.tier) = '3' THEN 'also_buys'
		WHEN TRIM(${linkAlias}.tier) = '4' THEN 'former'
		WHEN ${linkAlias}.relationship IN ('primary', 'secondary', 'also_buys', 'former') THEN ${linkAlias}.relationship
		ELSE 'also_buys'
	END)`;
}

const LEGAL_SUFFIXES = new Set([
	'inc',
	'incorporated',
	'llc',
	'ltd',
	'limited',
	'co',
	'corp',
	'corporation',
	'company',
	'lp',
	'llp',
	'plc',
]);

/**
 * The comparison key for a competitor name: lower case, accents and
 * punctuation removed, "&" read as "and", a leading "the" and trailing legal
 * suffixes ("Inc", "LLC", "Co") dropped, and spaces removed — so "Parmed ",
 * "PARMED" and "Parmed, Inc." are one key, as are "Amerisource Bergen" and
 * "AmerisourceBergen". Returns '' when the text has no letters or digits.
 *
 * Abbreviations ("MCK" for McKesson) are not guessed; they are what aliases
 * are for.
 */
export function normalizeCompetitorName(
	value: string | null | undefined,
): string {
	if (value == null) {
		return '';
	}

	const words = value
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/&/g, ' and ')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.split(' ')
		.filter((word) => word !== '');

	const core = [...words];

	if (core.length > 1 && core[0] === 'the') {
		core.shift();
	}

	while (core.length > 1 && LEGAL_SUFFIXES.has(core[core.length - 1] ?? '')) {
		core.pop();
	}

	return core.join('');
}

/** Levenshtein distance, giving up (returning `limit + 1`) once past `limit`. */
function boundedEditDistance(a: string, b: string, limit: number): number {
	if (Math.abs(a.length - b.length) > limit) {
		return limit + 1;
	}

	let previous = Array.from({ length: b.length + 1 }, (_, index) => index);

	for (let i = 1; i <= a.length; i++) {
		const current = [i];
		let rowMin = i;

		for (let j = 1; j <= b.length; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;
			const value = Math.min(
				(previous[j] ?? 0) + 1,
				(current[j - 1] ?? 0) + 1,
				(previous[j - 1] ?? 0) + cost,
			);
			current.push(value);
			rowMin = Math.min(rowMin, value);
		}

		if (rowMin > limit) {
			return limit + 1;
		}

		previous = current;
	}

	return previous[b.length] ?? limit + 1;
}

/**
 * True when two different keys are close enough to ask "did you mean": one
 * starts with the other ("ferguson" / "fergusonenterprises"), or they differ
 * by a typo (one edit from 5 characters, two from 9: "amerisouce" /
 * "amerisource"). Short keys only ever match exactly, since "abc" and "abd"
 * are as likely to be different companies as a typo.
 */
export function isSimilarCompetitorKey(a: string, b: string): boolean {
	if (a === '' || b === '' || a === b) {
		return false;
	}

	const shorter = a.length <= b.length ? a : b;
	const longer = a.length <= b.length ? b : a;

	if (shorter.length >= 4 && longer.startsWith(shorter)) {
		return true;
	}

	const limit = shorter.length >= 9 ? 2 : shorter.length >= 5 ? 1 : 0;

	return limit > 0 && boundedEditDistance(a, b, limit) <= limit;
}

export interface CompetitorNameCandidateModel {
	id: number;
	name: string;
	aliases: ReadonlyArray<string>;
}

export interface CompetitorNameMatchModel {
	id: number;
	name: string;
	/** The name or alias that matched. */
	matchedName: string;
	kind: 'exact' | 'similar';
}

/**
 * Competitors whose name or other name matches `input`, for the "did you
 * mean" prompt that keeps "Ferguson" and "Ferguson Enterprises" from becoming
 * two records. Exact key matches first, then similar ones; one entry per
 * competitor, and never `excludeId` (the record being edited).
 */
export function findCompetitorNameMatches(
	input: string,
	candidates: ReadonlyArray<CompetitorNameCandidateModel>,
	excludeId?: number | null,
): Array<CompetitorNameMatchModel> {
	const key = normalizeCompetitorName(input);

	if (key === '') {
		return [];
	}

	const exact: Array<CompetitorNameMatchModel> = [];
	const similar: Array<CompetitorNameMatchModel> = [];

	for (const candidate of candidates) {
		if (excludeId != null && candidate.id === excludeId) {
			continue;
		}

		const names = [candidate.name, ...candidate.aliases];
		const exactName = names.find(
			(name) => normalizeCompetitorName(name) === key,
		);

		if (exactName != null) {
			exact.push({
				id: candidate.id,
				name: candidate.name,
				matchedName: exactName,
				kind: 'exact',
			});
			continue;
		}

		const similarName = names.find((name) =>
			isSimilarCompetitorKey(normalizeCompetitorName(name), key),
		);

		if (similarName != null) {
			similar.push({
				id: candidate.id,
				name: candidate.name,
				matchedName: similarName,
				kind: 'similar',
			});
		}
	}

	return [...exact, ...similar];
}

/**
 * Words that mark competitor-price text as a remark about the price rather
 * than a supplier's name ("same price as always", "already approved price",
 * "N/A"). Matched as whole words.
 */
const NOTE_WORDS = new Set([
	'same',
	'approved',
	'price',
	'prices',
	'pricing',
	'priced',
	'always',
	'already',
	'quote',
	'quoted',
	'na',
	'none',
	'unknown',
	'tbd',
	'see',
	'matched',
	'match',
	'cheaper',
	'lower',
	'higher',
	'than',
	'was',
	'were',
	'per',
	'other',
	'misc',
]);

export type CompetitorTextClassificationModel =
	| { kind: 'blank' }
	| { kind: 'note' }
	| { kind: 'name'; name: string; key: string };

/**
 * What a free-text competitor entry on a competitive price is: a supplier's
 * name, a note typed into the name field, or nothing. Used by the competitive
 * price backfill, which links names to competitors and moves notes to the
 * note field. Text naming several suppliers ("KeySource / Parmed / KCP") is a
 * note too: one price cannot be from three competitors.
 */
export function classifyCompetitorText(
	value: string | null | undefined,
): CompetitorTextClassificationModel {
	const name = (value ?? '').trim().replace(/\s+/g, ' ');

	if (name === '') {
		return { kind: 'blank' };
	}

	const key = normalizeCompetitorName(name);

	if (key === '') {
		return { kind: 'note' };
	}

	if (/[/;|+]/.test(name) || /\s(?:or)\s/i.test(name)) {
		return { kind: 'note' };
	}

	// A comma lists several suppliers ("Parmed, KeySource") unless what follows
	// it is a legal suffix ("Parmed, Inc.").
	const commaParts = name
		.split(',')
		.map((part) => part.toLowerCase().replace(/[^a-z0-9]+/g, ''))
		.filter((part) => part !== '' && !LEGAL_SUFFIXES.has(part));

	if (commaParts.length > 1) {
		return { kind: 'note' };
	}

	if (/\$|\d+\.\d{2}\b|%/.test(name)) {
		return { kind: 'note' };
	}

	const words = name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.split(' ')
		.filter((word) => word !== '');

	if (words.length > 6 || words.some((word) => NOTE_WORDS.has(word))) {
		return { kind: 'note' };
	}

	return { kind: 'name', name, key };
}

/** One competitor's aggregate row as {@link buildCompetitorSummarySql} returns it. */
export interface CompetitorSummaryRowModel {
	wholesalerId: number | string;
	customerCount: number | string | null;
	primaryCount: number | string | null;
	secondaryCount: number | string | null;
	alsoBuysCount: number | string | null;
	estimatedAnnualSpend: number | string | null;
	estimatedCustomerCount: number | string | null;
	lastConfirmedDate: Date | string | null;
	priceCount: number | string | null;
	pricedItemCount: number | string | null;
	lastPriceDate: Date | string | null;
}

/** The stored summary columns on `wholesaler`. */
export interface CompetitorSummaryValuesModel {
	customerCount: number;
	primaryCount: number;
	secondaryCount: number;
	alsoBuysCount: number;
	estimatedAnnualSpend: string | null;
	estimatedCustomerCount: number;
	lastConfirmedDate: Date | null;
	priceCount: number;
	pricedItemCount: number;
	lastPriceDate: Date | null;
}

function toCount(value: number | string | null | undefined): number {
	const parsed = typeof value === 'number' ? value : Number(value ?? 0);

	return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0;
}

function toDateOrNull(value: Date | string | null | undefined): Date | null {
	if (value == null) {
		return null;
	}

	const date = value instanceof Date ? value : new Date(value);

	return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The stored summary values from one aggregate row. MySQL returns SUM and
 * decimal results as strings and NULL for a competitor with no rows, so every
 * value is normalized here. The spend stays a fixed 2-decimal string (the
 * decimal column's own form) and is NULL when no current link carries an
 * estimate, which the list shows as "not estimated" rather than $0.
 */
export function toCompetitorSummaryValues(
	row: CompetitorSummaryRowModel | null | undefined,
): CompetitorSummaryValuesModel {
	const estimatedCustomerCount = toCount(row?.estimatedCustomerCount);
	const spend =
		row?.estimatedAnnualSpend == null
			? null
			: Number(row.estimatedAnnualSpend);

	return {
		customerCount: toCount(row?.customerCount),
		primaryCount: toCount(row?.primaryCount),
		secondaryCount: toCount(row?.secondaryCount),
		alsoBuysCount: toCount(row?.alsoBuysCount),
		estimatedAnnualSpend:
			estimatedCustomerCount > 0 && spend != null && Number.isFinite(spend)
				? spend.toFixed(2)
				: null,
		estimatedCustomerCount,
		lastConfirmedDate: toDateOrNull(row?.lastConfirmedDate),
		priceCount: toCount(row?.priceCount),
		pricedItemCount: toCount(row?.pricedItemCount),
		lastPriceDate: toDateOrNull(row?.lastPriceDate),
	};
}

/**
 * The aggregate behind the stored summary values, for the given competitor
 * ids. Server-side SQL kept beside the rules it encodes so crm-api's
 * CompetitorSummaryService and the config-api backfill migration compute the
 * same numbers.
 *
 * Counts cover current (not former) links of live customers; a link is live
 * while its row exists (SM1 hard-deletes links, so `_deleted_date` is only a
 * belt-and-braces check). Prices count live rows linked to the competitor.
 * The ids are bound three times; pass them as `params`.
 */
export function buildCompetitorSummarySql(wholesalerIds: ReadonlyArray<number>): {
	sql: string;
	params: Array<unknown>;
} {
	const ids = wholesalerIds.filter((id) => Number.isInteger(id) && id > 0);
	const relationship = competitorRelationshipSql('cw');

	const sql = `
		SELECT
			w._id AS wholesalerId,
			links.customerCount,
			links.primaryCount,
			links.secondaryCount,
			links.alsoBuysCount,
			links.estimatedAnnualSpend,
			links.estimatedCustomerCount,
			links.lastConfirmedDate,
			prices.priceCount,
			prices.pricedItemCount,
			prices.lastPriceDate
		FROM wholesaler w
		LEFT JOIN (
			SELECT
				l.wholesalerId,
				SUM(l.rel <> 'former') AS customerCount,
				SUM(l.rel = 'primary') AS primaryCount,
				SUM(l.rel = 'secondary') AS secondaryCount,
				SUM(l.rel = 'also_buys') AS alsoBuysCount,
				SUM(CASE WHEN l.rel <> 'former' THEN l.estimatedAnnualSpend END) AS estimatedAnnualSpend,
				SUM(l.rel <> 'former' AND l.estimatedAnnualSpend IS NOT NULL) AS estimatedCustomerCount,
				MAX(l.confirmedDate) AS lastConfirmedDate
			FROM (
				SELECT
					cw._fk_wholesaler__id AS wholesalerId,
					${relationship} AS rel,
					cw.estimatedAnnualSpend,
					cw.confirmedDate
				FROM client_wholesaler cw
				INNER JOIN client c ON c._id = cw._fk_client__id AND c._deleted_date IS NULL
				WHERE cw._deleted_date IS NULL
					AND cw._fk_wholesaler__id IN (?)
			) l
			GROUP BY l.wholesalerId
		) links ON links.wholesalerId = w._id
		LEFT JOIN (
			SELECT
				cp.fk_wholesaler__id AS wholesalerId,
				COUNT(*) AS priceCount,
				COUNT(DISTINCT cp.fk_item__id) AS pricedItemCount,
				MAX(cp._created_date) AS lastPriceDate
			FROM competitive_price cp
			WHERE cp._deleted_date IS NULL
				AND cp.fk_wholesaler__id IN (?)
			GROUP BY cp.fk_wholesaler__id
		) prices ON prices.wholesalerId = w._id
		WHERE w._id IN (?)
	`;

	// An empty IN () is a syntax error; [0] matches no competitor.
	const bound = ids.length > 0 ? ids : [0];

	return { sql, params: [bound, bound, bound] };
}
